import { HttpClient } from '@wix/http-client';
import {
  ImageUploadHandlers,
  TEXT_ON_IMAGE_BRIGHTNESS,
  TEXT_ON_IMAGE_FLOWS,
  TextOnImageGetPhotoSceneParams,
  TextOnImageObjectDimensions,
  TextOnImagePhotosDefinition,
  TextOnImageResponse,
} from '../types';
import { getSkyFlowImage } from './skyFlow';
import { getDataVisualizationImage } from './dataVisualizationFlow';
import { getPortraitFlowImage } from './portraitFlow';
import { getNoPointOfInterestImage } from './noPointOfInterestFlow';
import { getObjectFlowImage } from './objectFlow';
import { ErrorReporter } from '@wix/editor-error-reporter';

const flowImageHandlers: Record<
  TEXT_ON_IMAGE_FLOWS,
  (
    httpClient: HttpClient,
    sectionName: string,
    params: TextOnImageGetPhotoSceneParams,
    imageUploadHandlers: ImageUploadHandlers,
  ) => Promise<TextOnImageResponse | undefined>
> = {
  [TEXT_ON_IMAGE_FLOWS.SKY]: getSkyFlowImage,
  [TEXT_ON_IMAGE_FLOWS.DATA_VISUALIZATION]: getDataVisualizationImage,
  [TEXT_ON_IMAGE_FLOWS.PORTRAIT]: getPortraitFlowImage,
  [TEXT_ON_IMAGE_FLOWS.NO_POINT_OF_INTEREST]: getNoPointOfInterestImage,
  [TEXT_ON_IMAGE_FLOWS.OBJECT]: getObjectFlowImage,
};

export const getImageByFlow = (
  httpClient: HttpClient,
  flow: TEXT_ON_IMAGE_FLOWS,
  sectionName: string,
  params: TextOnImageGetPhotoSceneParams,
  imageUploadHandlers: ImageUploadHandlers,
): Promise<TextOnImageResponse | undefined> | undefined => {
  const handler = flowImageHandlers[flow];
  if (!handler) return;
  return handler(httpClient, sectionName, params, imageUploadHandlers);
};

export const applyColorOverlayAndGetFile = async (
  imageUrl: string,
  color: string,
  brightness: TEXT_ON_IMAGE_BRIGHTNESS,
): Promise<string | undefined> => {
  const operationType: GlobalCompositeOperation =
    brightness === TEXT_ON_IMAGE_BRIGHTNESS.BRIGHT ? 'screen' : 'color-burn';

  try {
    const image = await loadImage(imageUrl);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.drawImage(image, 0, 0);
    ctx.globalCompositeOperation = operationType;
    ctx.fillStyle = `rgba(${color},0.75)`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';

    return await canvasToBlobUrl(canvas);
  } catch (error) {
    reportTextOnImageError(
      `DataVisualizationFlow: Failed to apply rgb overlay: ${error}`,
    );
  }
};

export const applyImageOverlayAndGetFile = async (
  backgroundImage: string,
  objectImage: string,
  dimensions?: TextOnImageObjectDimensions,
): Promise<string | undefined> => {
  try {
    const background = await loadImage(backgroundImage);
    const object = await loadImage(objectImage);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = background.width;
    canvas.height = background.height;

    ctx.drawImage(background, 0, 0);

    const objectWidth = dimensions?.width || object.width / 3;
    const objectHeight = dimensions?.height || object.height / 3;
    const objectX = dimensions?.pxLeft || background.width - objectWidth - 200;
    const objectY =
      dimensions?.pxTop || (background.height - objectHeight) / 1.5;

    ctx.drawImage(object, objectX, objectY, objectWidth, objectHeight);

    return await canvasToBlobUrl(canvas);
  } catch (error) {
    console.error(`Failed to apply image overlay: ${error}`);
  }
};

export const applyOpacityAndGetFile = async (
  imageUrl: string,
  opacity: number = 0.85,
): Promise<string | undefined> => {
  try {
    const image = await loadImage(imageUrl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.globalAlpha = opacity;
    ctx.drawImage(image, 0, 0);
    ctx.globalAlpha = 1.0;

    return await canvasToBlobUrl(canvas);
  } catch (error) {
    reportTextOnImageError(`Failed to apply opacity: ${error}`);
  }
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
  });
};

const canvasToBlobUrl = (canvas: HTMLCanvasElement): Promise<string> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty or could not be converted to Blob.'));
        return;
      }
      const url = URL.createObjectURL(blob);
      resolve(url);
    }, 'image/png');
  });
};

export const getStaticImageUrl = (uri: string): string => {
  const wixStaticBaseUrl = 'https://static.wixstatic.com/media/';
  return `${wixStaticBaseUrl}${uri}`;
};

export const reportTextOnImageError = (error: string) => {
  ErrorReporter.captureException(error, {
    tags: {
      siteGenerationAiErrorResponse: true,
      siteGenerationTextOnImage: true,
    },
  });
  throw new Error(error);
};

export const getImagesBySectionId = (
  sectionId?: string,
  images?: TextOnImagePhotosDefinition[],
): TextOnImagePhotosDefinition[] => {
  if (!images?.length || !sectionId) {
    return [];
  }

  const filteredImages = images.filter(
    (image) => image.sections?.some((section) => section._id === sectionId),
  );

  return filteredImages.length > 0 ? filteredImages : images;
};

export const getImagesByBrightness = (
  brightness?: string,
  images?: TextOnImagePhotosDefinition[],
): TextOnImagePhotosDefinition[] => {
  if (!images?.length || !brightness) {
    return [];
  }

  const filteredImages = images.filter(
    (image) => image.brightness === brightness,
  );

  return filteredImages.length > 0 ? filteredImages : images;
};
